import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import UsersTabContent from '../components/OrganizationUsers/UsersTabContent';
import InvitationsTabContent from '../components/OrganizationUsers/InvitationsTabContent';

/**
 * Page for managers to view and invite users for a specific organization.
 * Path: /organizations/:organization_id/users/:tab?
 */
export default function OrganizationUsersPage() {
  const { organization_id, tab } = useParams();
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(tab === 'invitations' ? 1 : 0);

  const getOrganizationTypeLabel = (org) => {
    if (!org) return 'Organization';
    if (org.is_lab) return 'Lab';
    if (org.is_practice) return 'Practice';
    if (org.is_collection_site) return 'Collection Site';
    return 'Organization';
  };

  // Load organization to display name
  useEffect(() => {
    if (!tokens?.id_token || !organization_id) return;
    setLoading(true);
    fetch(`${apiBase}/organizations/${organization_id}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => setOrganization(data || null))
      .catch(() => setOrganization(null))
      .finally(() => setLoading(false));
  }, [tokens, organization_id]);

  const handleTabChange = (_, newValue) => {
    setTabIndex(newValue);
    const path = `/organizations/${organization_id}/users${newValue === 1 ? '/invitations' : ''}`;
    navigate(path);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h4">
          {organization?.name || 'Organization'} {tabIndex === 1 ? 'Invitations' : 'Users'}
        </Typography>
        <Chip
          size="small"
          label={getOrganizationTypeLabel(organization)}
          variant="outlined"
        />
      </Box>
      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Users" />
        <Tab label="Invitations" />
      </Tabs>
      {tabIndex === 0 && <UsersTabContent organizationId={organization_id} organization={organization} />}
      {tabIndex === 1 && (
        <InvitationsTabContent
          organizationId={organization_id}
          organization={organization}
        />
      )}
    </Container>
  );
}
